export interface LocalLanding {
  id: string;
  language: 'fr' | 'en' | 'es' | 'pt';
  slug: string;
  title: string;
  metaDescription: string;
  heroTitle: string;
  heroSubtitle: string;
  location: {
    name: string;
    type: 'district' | 'monument' | 'area';
    coordinates: {
      lat: number;
      lng: number;
    };
    distance: string;
    transportOptions: string[];
  };
  content: {
    intro: string;
    highlights: string[];
    menu: {
      title: string;
      items: {
        name: string;
        description: string;
        price: string;
      }[];
    };
    testimonials: {
      author: string;
      rating: number;
      text: string;
      date: string;
      source: string;
    }[];
    faq: {
      question: string;
      answer: string;
    }[];
  };
  cta: {
    primary: {
      text: string;
      url: string;
    };
    secondary: {
      text: string;
      url: string;
    };
  };
  images: {
    hero: string;
    gallery: string[];
    location: string;
  };
  seoData: {
    targetedKeywords: string[];
    estimatedVolume: number;
    competition: number;
    opportunity: number;
  };
}

// Mock data pour Le 39V
export const localLandings39V: LocalLanding[] = [
  // Landing EN - Tour Eiffel
  {
    id: 'landing_39v_en_1',
    language: 'en',
    slug: 'fine-dining-near-eiffel-tower-paris',
    title: 'Luxury Restaurant Near Eiffel Tower | Le 39V Paris',
    metaDescription: 'Experience fine dining with Eiffel Tower views at Le 39V. Michelin-quality cuisine, romantic atmosphere, and stunning Paris panorama. Book your table today.',
    heroTitle: 'Fine Dining Experience Near the Eiffel Tower',
    heroSubtitle: 'Exceptional French Cuisine with Paris\' Most Iconic View',
    location: {
      name: 'Eiffel Tower',
      type: 'monument',
      coordinates: {
        lat: 48.8584,
        lng: 2.2945
      },
      distance: '800m',
      transportOptions: ['Metro 6 - Trocadéro', 'Metro 9 - Iéna', 'Bus 22, 30, 32']
    },
    content: {
      intro: 'Le 39V offers an unforgettable dining experience just steps away from the Eiffel Tower. Our restaurant combines contemporary French cuisine with breathtaking views of Paris\' most famous landmark.',
      highlights: [
        'Panoramic views of the Eiffel Tower',
        'Michelin-quality French cuisine',
        'Elegant rooftop setting',
        'Expert wine pairing',
        'Private dining options'
      ],
      menu: {
        title: 'Tasting Menu Highlights',
        items: [
          {
            name: 'Blue Lobster',
            description: 'Brittany blue lobster, citrus butter, seasonal vegetables',
            price: '€85'
          },
          {
            name: 'Wagyu Beef',
            description: 'Grade A5 Wagyu beef, truffle sauce, potato mousseline',
            price: '€95'
          },
          {
            name: 'Soufflé',
            description: 'Grand Marnier soufflé, vanilla ice cream',
            price: '€25'
          }
        ]
      },
      testimonials: [
        {
          author: 'James W.',
          rating: 5,
          text: 'Incredible dining experience with the most amazing view of the Eiffel Tower. The food was exceptional.',
          date: '2024-01-15',
          source: 'TripAdvisor'
        },
        {
          author: 'Sarah M.',
          rating: 5,
          text: 'Perfect spot for a romantic dinner. The service was impeccable and the view is unbeatable.',
          date: '2024-01-10',
          source: 'Google Reviews'
        }
      ],
      faq: [
        {
          question: 'What is the best time to visit for Eiffel Tower views?',
          answer: 'We recommend booking a table for 7:30 PM to enjoy both sunset views and the Tower\'s evening illumination.'
        },
        {
          question: 'Is reservation required?',
          answer: 'Yes, we strongly recommend making a reservation, especially for dinner service and window tables.'
        },
        {
          question: 'Do you offer vegetarian options?',
          answer: 'Yes, we offer a full vegetarian tasting menu. Please inform us of any dietary requirements when booking.'
        }
      ]
    },
    cta: {
      primary: {
        text: 'Book a Table',
        url: '/en/reservations'
      },
      secondary: {
        text: 'View Menu',
        url: '/en/menu'
      }
    },
    images: {
      hero: '/images/39v-eiffel-view.jpg',
      gallery: [
        '/images/39v-interior-1.jpg',
        '/images/39v-dishes-1.jpg',
        '/images/39v-terrace-1.jpg'
      ],
      location: '/images/39v-map-eiffel.jpg'
    },
    seoData: {
      targetedKeywords: [
        'restaurant near eiffel tower paris',
        'fine dining eiffel tower view',
        'best restaurants eiffel tower paris',
        'luxury restaurant paris eiffel tower'
      ],
      estimatedVolume: 12500,
      competition: 78,
      opportunity: 85
    }
  },

  // Landing ES - Champs-Élysées
  {
    id: 'landing_39v_es_1',
    language: 'es',
    slug: 'restaurante-cerca-champs-elysees-paris',
    title: 'Restaurante de Lujo Cerca de Champs-Élysées | Le 39V París',
    metaDescription: 'Descubre la alta gastronomía francesa cerca de los Champs-Élysées en Le 39V. Cocina excepcional y vistas panorámicas de París. Reserve su mesa hoy.',
    heroTitle: 'Experiencia Gastronómica en los Champs-Élysées',
    heroSubtitle: 'Alta Cocina Francesa en el Corazón de París',
    location: {
      name: 'Champs-Élysées',
      type: 'area',
      coordinates: {
        lat: 48.8698,
        lng: 2.3075
      },
      distance: '500m',
      transportOptions: ['Metro 1 - George V', 'Metro 9 - Saint-Philippe-du-Roule']
    },
    content: {
      intro: 'Le 39V ofrece una experiencia culinaria excepcional a pasos de los Champs-Élysées. Nuestro restaurante combina la mejor gastronomía francesa con vistas espectaculares de París.',
      highlights: [
        'Ubicación privilegiada',
        'Cocina francesa contemporánea',
        'Terraza panorámica',
        'Carta de vinos excepcional',
        'Servicio personalizado'
      ],
      menu: {
        title: 'Menú Degustación',
        items: [
          {
            name: 'Bogavante Azul',
            description: 'Bogavante de Bretaña, mantequilla cítrica, verduras de temporada',
            price: '€85'
          },
          {
            name: 'Ternera Wagyu',
            description: 'Ternera Wagyu A5, salsa de trufa, puré de patata',
            price: '€95'
          }
        ]
      },
      testimonials: [
        {
          author: 'Carlos R.',
          rating: 5,
          text: 'Una experiencia gastronómica inolvidable. La vista y la comida son excepcionales.',
          date: '2024-01-20',
          source: 'TripAdvisor'
        }
      ],
      faq: [
        {
          question: '¿Es necesario reservar?',
          answer: 'Sí, recomendamos reservar con anticipación, especialmente para cenas y mesas con vista.'
        },
        {
          question: '¿Tienen menú vegetariano?',
          answer: 'Sí, ofrecemos un menú degustación vegetariano completo.'
        }
      ]
    },
    cta: {
      primary: {
        text: 'Reservar Mesa',
        url: '/es/reservas'
      },
      secondary: {
        text: 'Ver Menú',
        url: '/es/menu'
      }
    },
    images: {
      hero: '/images/39v-champs-view.jpg',
      gallery: [
        '/images/39v-interior-2.jpg',
        '/images/39v-dishes-2.jpg',
        '/images/39v-terrace-2.jpg'
      ],
      location: '/images/39v-map-champs.jpg'
    },
    seoData: {
      targetedKeywords: [
        'restaurante cerca champs elysees',
        'restaurante lujo paris',
        'mejor restaurante paris champs elysees',
        'restaurante vista paris'
      ],
      estimatedVolume: 8500,
      competition: 72,
      opportunity: 82
    }
  }
];

// Mock data pour Café Max - Analyse en cours
// Landing pages locales Café Max
export const localLandingsCafeMax: LocalLanding[] = [
  // Landing FR - Bistrot Invalides (PRIORITÉ)
  {
    id: 'landing_cm_fr_1',
    language: 'fr',
    slug: 'bistrot-invalides-paris-7eme',
    title: 'Bistrot Invalides Paris | Café Max - Restaurant Traditionnel 7ème',
    metaDescription: 'Découvrez le Café Max, bistrot authentique près des Invalides. Cuisine française traditionnelle, terrasse ensoleillée. Réservation en ligne.',
    heroTitle: 'Bistrot Authentique aux Invalides',
    heroSubtitle: 'Cuisine Traditionnelle Française depuis 1941',
    location: {
      name: 'Les Invalides',
      type: 'monument' as const,
      coordinates: { lat: 48.8566, lng: 2.3126 },
      distance: '300m',
      transportOptions: ['Métro Invalides (ligne 8, 13)', 'Bus 69, 82, 87']
    },
    content: {
      intro: 'Le Café Max est un bistrot historique situé à deux pas des Invalides, dans le 7ème arrondissement de Paris.',
      highlights: [
        'Cuisine française traditionnelle',
        'Terrasse ensoleillée',
        'Histoire de la Résistance',
        'Chef Frédéric Vardon'
      ],
      menu: {
        title: 'Nos Spécialités',
        items: [
          { name: 'Confit de Canard', description: 'Pommes sarladaises', price: '26€' },
          { name: 'Œufs Mimosa', description: 'Classique maison', price: '12€' },
          { name: 'Formule Déjeuner', description: 'Entrée + Plat + Dessert', price: '24€' }
        ]
      },
      testimonials: [
        {
          author: 'Marie L.',
          rating: 5,
          text: 'Bistrot authentique avec une histoire fascinante. Cuisine excellente !',
          date: '2025-11-10',
          source: 'Google'
        }
      ],
      faq: [
        {
          question: 'Où se trouve le Café Max ?',
          answer: '7 avenue de la Motte-Picquet, 75007 Paris, à 3 minutes des Invalides.'
        }
      ]
    },
    cta: {
      primary: { text: 'Réserver une table', url: '/fr/reserver' },
      secondary: { text: 'Voir le menu', url: '/fr/menu' }
    },
    images: {
      hero: '/images/cafe-max-terrasse.jpg',
      gallery: ['/images/cafe-max-interior.jpg'],
      location: '/images/cafe-max-map.jpg'
    },
    seoData: {
      targetedKeywords: ['bistrot invalides', 'restaurant invalides', 'café max'],
      estimatedVolume: 4500,
      competition: 65,
      opportunity: 95
    }
  }
];

// Landing pages locales La Fontaine Gaillon
export const localLandingsFontaineGaillon: LocalLanding[] = [
  {
    id: 'landing_fg_fr_1',
    language: 'fr',
    slug: 'restaurant-terrasse-paris-opera',
    title: 'Restaurant Terrasse Néoclassique Paris Opéra | La Fontaine Gaillon',
    metaDescription: 'Découvrez La Fontaine Gaillon, restaurant gastronomique avec terrasse unique autour d\'une fontaine néoclassique. Salons privés historiques. Réservez votre table.',
    heroTitle: 'Terrasse Néoclassique Iconique au Cœur de Paris',
    heroSubtitle: 'Gastronomie Bourgeoise Contemporaine depuis 1802',
    location: {
      name: 'Opéra Garnier',
      type: 'monument' as const,
      coordinates: { lat: 48.8720, lng: 2.3318 },
      distance: '400m',
      transportOptions: ['Métro Quatre Septembre (3)', 'Métro Opéra (3, 7, 8)', 'Bus 20, 27, 29, 39']
    },
    content: {
      intro: 'La Fontaine Gaillon est une institution gastronomique parisienne fondée en 1802, récemment réinventée par le Fitz Group. Notre terrasse végétalisée autour d\'une fontaine néoclassique offre un cadre unique pour vos déjeuners et dîners.',
      highlights: [
        'Terrasse néoclassique avec fontaine unique à Paris',
        'Cuisine bourgeoise contemporaine raffinée',
        '6 salons privés historiques (Mansart, Krug, Rémy Martin...)',
        'Décor par Lázaro Rosa-Violán',
        'Institution depuis 1802'
      ],
      menu: {
        title: 'Nos Signatures',
        items: [
          { name: 'Homard Breton', description: 'Beurre citronné, légumes de saison', price: '48€' },
          { name: 'Turbot Sauvage', description: 'Sauce vin blanc, purée de céleri', price: '52€' },
          { name: 'Soufflé Maison', description: 'Grand Marnier, glace vanille Bourbon', price: '18€' }
        ]
      },
      testimonials: [
        {
          author: 'Jean-Marc D.',
          rating: 5,
          text: 'La terrasse est absolument magnifique. Service impeccable et cuisine raffinée. Un vrai bijou caché à Paris.',
          date: '2024-11-15',
          source: 'Google'
        },
        {
          author: 'Sophie L.',
          rating: 5,
          text: 'Ambiance chaleureuse et élégante. La fontaine néoclassique crée une atmosphère unique. Parfait pour un dîner d\'affaires.',
          date: '2024-10-28',
          source: 'TheFork'
        },
        {
          author: 'Victoria H.',
          rating: 5,
          text: 'Out of this world! The terrace with the gorgeous neoclassical fountain is breathtaking. Food was exceptional.',
          date: '2024-09-12',
          source: 'TripAdvisor'
        }
      ],
      faq: [
        {
          question: 'La terrasse est-elle chauffée en hiver ?',
          answer: 'Oui, notre terrasse est équipée de chauffages et de couvertures pour votre confort toute l\'année.'
        },
        {
          question: 'Proposez-vous des salons privés ?',
          answer: 'Oui, nous disposons de 6 salons privés historiques (Mansart, Krug, Rémy Martin, Gaillon, Louis Le Grand, Emile Zola) pouvant accueillir de 8 à 60 personnes.'
        },
        {
          question: 'Quelle est l\'histoire de La Fontaine Gaillon ?',
          answer: 'Fondé en 1802, notre établissement est un ancien hôtel particulier. La fontaine néoclassique date du XVIIIe siècle. Anciennement propriété de Gérard Depardieu, le restaurant a été réinventé par le Fitz Group avec le célèbre designer Lázaro Rosa-Violán.'
        }
      ]
    },
    cta: {
      primary: { text: 'Réserver une table', url: '/fr/reservation' },
      secondary: { text: 'Privatisation & Salons', url: '/fr/salons-prives' }
    },
    images: {
      hero: '/images/fontaine-gaillon-terrasse.jpg',
      gallery: ['/images/fontaine-gaillon-fontaine.jpg', '/images/fontaine-gaillon-salon.jpg', '/images/fontaine-gaillon-plats.jpg'],
      location: '/images/fontaine-gaillon-map.jpg'
    },
    seoData: {
      targetedKeywords: ['restaurant terrasse paris 2', 'restaurant terrasse néoclassique', 'salon privé paris opéra', 'restaurant gastronomique place gaillon'],
      estimatedVolume: 6800,
      competition: 65,
      opportunity: 88
    }
  },
  {
    id: 'landing_fg_fr_2',
    language: 'fr',
    slug: 'privatisation-restaurant-paris-salons-prives',
    title: 'Privatisation Restaurant Paris | Salons Privés La Fontaine Gaillon',
    metaDescription: 'Privatisez La Fontaine Gaillon pour vos événements corporate. 6 salons historiques, terrasse unique, service d\'excellence. Devis gratuit.',
    heroTitle: 'Salons Privés & Privatisation',
    heroSubtitle: '6 Salons Historiques pour vos Événements d\'Exception',
    location: {
      name: 'Quartier Bourse',
      type: 'area' as const,
      coordinates: { lat: 48.8693, lng: 2.3353 },
      distance: 'Centre',
      transportOptions: ['Métro Quatre Septembre', 'Métro Bourse', 'RER Auber']
    },
    content: {
      intro: 'Organisez vos événements corporate, dîners d\'affaires et célébrations privées dans nos 6 salons historiques ou privatisez l\'intégralité du restaurant.',
      highlights: [
        '6 salons privés de 8 à 60 personnes',
        'Privatisation totale possible',
        'Menus personnalisables',
        'Équipements audiovisuels disponibles',
        'Service traiteur haut de gamme'
      ],
      menu: {
        title: 'Formules Privatisation',
        items: [
          { name: 'Cocktail Déjeunatoire', description: '15 pièces par personne + 2 boissons', price: '65€/pers' },
          { name: 'Menu Déjeuner', description: 'Entrée + Plat + Dessert + Café', price: '75€/pers' },
          { name: 'Menu Prestige', description: 'Menu 4 services + accord mets-vins', price: '125€/pers' }
        ]
      },
      testimonials: [
        {
          author: 'Pierre M. - DRH',
          rating: 5,
          text: 'Parfait pour notre séminaire. Le salon Mansart est magnifique et le service irréprochable.',
          date: '2024-10-05',
          source: 'Corporate'
        }
      ],
      faq: [
        {
          question: 'Quelle est la capacité des salons ?',
          answer: 'Nos salons accueillent de 8 à 60 personnes. La privatisation totale peut accueillir jusqu\'à 120 convives.'
        },
        {
          question: 'Proposez-vous des équipements techniques ?',
          answer: 'Oui, projecteur, écran, sonorisation et WiFi haut débit sont disponibles.'
        }
      ]
    },
    cta: {
      primary: { text: 'Demander un devis', url: '/fr/contact-privatisation' },
      secondary: { text: 'Voir nos salons', url: '/fr/salons' }
    },
    images: {
      hero: '/images/fontaine-gaillon-salon-mansart.jpg',
      gallery: ['/images/fontaine-gaillon-salons.jpg'],
      location: '/images/fontaine-gaillon-plan.jpg'
    },
    seoData: {
      targetedKeywords: ['privatisation restaurant paris', 'salon privé restaurant paris', 'dîner affaires paris opéra', 'séminaire restaurant paris'],
      estimatedVolume: 4200,
      competition: 58,
      opportunity: 85
    }
  }
];

// Landing pages locales Hollywood Savoy
export const localLandingsHollywoodSavoy: LocalLanding[] = [
  {
    id: 'landing_hs_fr_1',
    language: 'fr',
    slug: 'restaurant-festif-paris-live-show',
    title: 'Restaurant Festif Paris avec Live Show | Hollywood Savoy',
    metaDescription: 'Découvrez le Hollywood Savoy, fusion unique de gastronomie raffinée et ambiance clubbing. Tomahawk, Caviar, DJ sets. L\'expérience festive chic parisienne.',
    heroTitle: 'L\'Expérience Fun Dining Parisienne',
    heroSubtitle: 'Gastronomie Raffinée + Ambiance Clubbing depuis 1982',
    location: {
      name: 'Quartier Bourse',
      type: 'area' as const,
      coordinates: { lat: 48.8693, lng: 2.3390 },
      distance: 'Centre',
      transportOptions: ['Métro Bourse (3)', 'Métro Sentier (3)', 'Métro Grands Boulevards (8, 9)', 'Bus 20, 39, 48']
    },
    content: {
      intro: 'Le Hollywood Savoy renaît comme l\'adresse iconique du Paris festif des années 80. Fusion unique de gastronomie raffinée (Tomahawk 1.3kg, Caviar) et d\'ambiance clubbing avec Live Shows et DJ sets.',
      highlights: [
        'Fusion gastronomie & clubbing unique à Paris',
        'Tomahawk de bœuf 1.3kg signature',
        'Caviar et champagne',
        'Live Shows & DJ sets',
        'Décor Art Déco Orient-Express',
        'Service exceptionnel (9.3/10)'
      ],
      menu: {
        title: 'Nos Signatures',
        items: [
          { name: 'Tomahawk de Bœuf 1.3kg', description: 'À partager, légumes grillés, sauce au poivre', price: '58€' },
          { name: 'Caviar Impérial', description: 'Osciètre, blinis, crème fraîche', price: '85€' },
          { name: 'Lobster Roll', description: 'Street-food haut de gamme', price: '32€' },
          { name: 'Poulet Mayonnaise', description: 'Revisité, herbes fraîches', price: '24€' }
        ]
      },
      testimonials: [
        {
          author: 'Marc & Lisa',
          rating: 5,
          text: 'Superbe soirée ! Ambiance lumineuse et festive. Nissrine nous a parfaitement accueillis. Food: 5, Service: 5, Atmosphere: 5.',
          date: '2024-11-22',
          source: 'Google'
        },
        {
          author: 'Thomas B.',
          rating: 5,
          text: 'Le service est incroyable, merci Mouloud pour cette soirée exceptionnelle ! Ambiance festive et cuisine délicieuse.',
          date: '2024-10-18',
          source: 'Google'
        },
        {
          author: 'Caroline M.',
          rating: 4,
          text: 'Très belle découverte ! Ambiance festive, peut-être un peu bruyant pour certains mais c\'est le concept.',
          date: '2024-09-30',
          source: 'TheFork'
        }
      ],
      faq: [
        {
          question: 'Quelle est l\'ambiance du Hollywood Savoy ?',
          answer: 'Le Hollywood Savoy est un lieu vivant et festif conçu pour célébrer. Nous proposons une expérience "Fun Dining" unique combinant gastronomie raffinée et ambiance clubbing avec DJ sets et Live Shows.'
        },
        {
          question: 'Y a-t-il des Live Shows tous les soirs ?',
          answer: 'Oui, nous proposons des performances live régulières. Consultez notre programmation sur notre site ou appelez-nous pour plus d\'informations.'
        },
        {
          question: 'Le Hollywood Savoy convient-il pour un dîner d\'affaires ?',
          answer: 'Nos déjeuners sont parfaits pour les repas d\'affaires dans une ambiance plus calme. Le soir, l\'ambiance devient festive et clubbing.'
        },
        {
          question: 'Quelle est la politique photo ?',
          answer: 'Pour préserver l\'intimité de notre Savoy Club, nous demandons "No Photos Please" après 22h. Photos autorisées pendant le dîner.'
        }
      ]
    },
    cta: {
      primary: { text: 'Réserver une table', url: '/fr/reservation' },
      secondary: { text: 'Voir la programmation', url: '/fr/events' }
    },
    images: {
      hero: '/images/hollywood-savoy-ambiance.jpg',
      gallery: ['/images/hollywood-savoy-tomahawk.jpg', '/images/hollywood-savoy-bar.jpg', '/images/hollywood-savoy-dj.jpg'],
      location: '/images/hollywood-savoy-map.jpg'
    },
    seoData: {
      targetedKeywords: ['restaurant festif paris', 'restaurant avec live show', 'dîner clubbing paris', 'restaurant ambiance festive paris', 'tomahawk restaurant paris'],
      estimatedVolume: 8500,
      competition: 72,
      opportunity: 90
    }
  },
  {
    id: 'landing_hs_fr_2',
    language: 'fr',
    slug: 'hollywood-savoy-club-soiree-paris',
    title: 'Hollywood Savoy Club | Soirée Festive Paris Années 80',
    metaDescription: 'Vivez la renaissance du temple festif parisien des années 80. DJ sets, décor Art Déco, champagne. Le Savoy Club est de retour.',
    heroTitle: 'Le Savoy Club : Renaissance d\'une Légende',
    heroSubtitle: 'La Nuit Parisienne réinventée',
    location: {
      name: 'Notre Dame des Victoires',
      type: 'area' as const,
      coordinates: { lat: 48.8683, lng: 2.3410 },
      distance: 'Centre Paris',
      transportOptions: ['Métro Bourse', 'Métro Sentier', 'Bus 20, 39, 48']
    },
    content: {
      intro: 'Le Hollywood Savoy Club renaît comme l\'adresse iconique du Paris festif des années 80. Là où Caroline Nin et les grandes voix du jazz ont débuté, vivez une soirée fusion unique entre gastronomie et clubbing.',
      highlights: [
        'Histoire iconique années 80 ressuscitée',
        'DJ sets et Live Shows',
        'Décor Art Déco sexy',
        'Champagne et cocktails signature',
        'Dress code chic décontracté',
        'No Photos Please (intimité préservée)'
      ],
      menu: {
        title: 'Cocktails & Champagnes',
        items: [
          { name: 'Hollywood Spritz', description: 'Aperol, Prosecco, orange', price: '16€' },
          { name: 'Savoy Martini', description: 'Vodka premium, vermouth dry', price: '18€' },
          { name: 'Champagne Carte', description: 'Sélection maison', price: 'à partir de 85€' }
        ]
      },
      testimonials: [
        {
          author: 'Julie & Friends',
          rating: 5,
          text: 'Ambiance incroyable ! On a adoré l\'énergie du lieu. Perfect for celebrating life!',
          date: '2024-11-08',
          source: 'Instagram'
        }
      ],
      faq: [
        {
          question: 'À partir de quelle heure commence le club ?',
          answer: 'L\'ambiance club démarre généralement après 22h avec nos DJ sets. Réservation restaurant fortement conseillée.'
        },
        {
          question: 'Y a-t-il un dress code ?',
          answer: 'Nous recommandons une tenue chic décontractée. Le Hollywood Savoy cultive une ambiance élégante et festive.'
        }
      ]
    },
    cta: {
      primary: { text: 'Liste VIP', url: '/fr/vip-list' },
      secondary: { text: 'Dîner + Soirée', url: '/fr/experience-complete' }
    },
    images: {
      hero: '/images/hollywood-savoy-club-night.jpg',
      gallery: ['/images/hollywood-savoy-dj.jpg', '/images/hollywood-savoy-bar-night.jpg'],
      location: '/images/hollywood-savoy-plan.jpg'
    },
    seoData: {
      targetedKeywords: ['club paris', 'soirée paris bourse', 'clubbing chic paris', 'restaurant club paris', 'soirée années 80 paris'],
      estimatedVolume: 5200,
      competition: 68,
      opportunity: 82
    }
  }
];

export const getLocalLandings = (restaurantId: string): LocalLanding[] => {
  if (restaurantId === '39v') return localLandings39V;
  if (restaurantId === 'cafe-max') return localLandingsCafeMax;
  if (restaurantId === 'fontaine-gaillon') return localLandingsFontaineGaillon;
  if (restaurantId === 'hollywood-savoy') return localLandingsHollywoodSavoy;
  return localLandings39V;
};

export const getLocalLandingsByLanguage = (restaurantId: string, language: 'fr' | 'en' | 'es' | 'pt'): LocalLanding[] => {
  const allLandings = getLocalLandings(restaurantId);
  return allLandings.filter(landing => landing.language === language);
};

export const getLandingStats = (restaurantId: string) => {
  const allLandings = getLocalLandings(restaurantId);
  
  return {
    totalLandings: allLandings.length,
    byLanguage: {
      fr: allLandings.filter(l => l.language === 'fr').length,
      en: allLandings.filter(l => l.language === 'en').length,
      es: allLandings.filter(l => l.language === 'es').length,
      pt: allLandings.filter(l => l.language === 'pt').length
    },
    totalTargetedVolume: allLandings.reduce((sum, l) => sum + l.seoData.estimatedVolume, 0),
    averageOpportunity: Math.round(
      allLandings.reduce((sum, l) => sum + l.seoData.opportunity, 0) / allLandings.length
    )
  };
};
